import { throttle, includes } from 'lodash';
import * as React from 'react';
import ReactDOM from 'react-dom';
import idx from 'idx';

import createChainedFunction from 'utils/createChainedFunction';
import cx from 'classnames';
import DOM from 'lib/DOM';
import findScrollParent from 'utils/findScrollParent';
import findZIndex from 'utils/findZIndex';
import isFixed from 'utils/isFixed';
import KeyState from 'lib/KeyState';

import './styles/LayerTrigger.scss';

type Diff<T, U> = T extends U ? never : T;

const PLACEMENTS: OppositeAlignment[] = ['top', 'right', 'bottom', 'left'];
const HORIZONTAL_ALIGNMENTS: Alignment[] = ['left', 'middle', 'right'];
const VERTICAL_ALIGNMENTS: Alignment[] = ['top', 'middle', 'bottom'];
declare type OppositeAlignment = Diff<Alignment, 'middle'>;
const OPPOSITES: { [key in OppositeAlignment]: Alignment } = {
  top: 'bottom',
  right: 'left',
  bottom: 'top',
  left: 'right',
};

/**
 * Check if value one is inside or equal to the of value
 *
 * @param {string} one
 * @param {string|array} of
 * @returns {boolean}
 */
function isOneOf(one: any, of: TriggerTypes[] | TriggerTypes) {
  if (Array.isArray(of)) {
    return of.indexOf(one) >= 0;
  }
  return one === of;
}

/**
 * ## Displays a [Layer](/internal/components/Layer) when a user interacts with a trigger.
 *
 * # Troubleshooting
 *
 * LayerTrigger does not play well with pure functional components. If you get errors about
 * offsetWidth, or it fails to render the layer in the right
 * position, use a class.
 *
 * # Implementation
 *
 * You may set a preferred `alignment` & `placement`, but LayerTrigger will determine whether to
 * override based on if the Layer can fit within the viewport.
 *
 * Layers can be programmatically triggered using `trigger="manual"` on the LayerTrigger & call
 * `showLayer()` and `hideLayer()` on the LayerTrigger instance.
 *
 * ```js
 * <LayerTrigger
 *   ref="trigger"
 *   trigger={['click', 'manual']}>
 *   <Button>Click<Button>
 * </LayerTrigger>
 * <Button onClick={() => this.refs.trigger.showLayer()}>
 *   Trigger
 * </Button>
 * ```
 *
 * # Common usage
 *
 * ```example
 * <Buttons>
 *   <LayerTrigger
 *     layer={
 *       <Hovercard>
 *         <HovercardBody>
 *           Hovercard
 *         </HovercardBody>
 *       </Hovercard>
 *     }>
 *     <Button>Hover</Button>
 *   </LayerTrigger>
 *   <LayerTrigger
 *     layer={
 *       <Hovercard>
 *         <HovercardBody>
 *           Hovercard
 *         </HovercardBody>
 *       </Hovercard>
 *     }
 *     placement="top"
 *     trigger="click">
 *     <Button>Click</Button>
 *   </LayerTrigger>
 * </Buttons>
 * ```
 *
 * ```example
 * <LayerTrigger
 *   layer={
 *     <Hovercard>
 *       <HovercardBody>
 *         Hovercard
 *       </HovercardBody>
 *     </Hovercard>
 *   }
 *   useContextWidth>
 *   <Button>Hover</Button>
 * </LayerTrigger>
 * ```
 *
 * # defaultLayerShown
 *
 * ```example
 * <LayerTrigger
 *   defaultLayerShown
 *   layer={
 *     <Hovercard>
 *       <HovercardBody>
 *         Hovercard
 *       </HovercardBody>
 *     </Hovercard>
 *   }
 *   trigger="manual">
 *   <Button>Button</Button>
 * </LayerTrigger>
 * ```
 *
 *
 * # TODO
 * * Consider enforcing `focus`-able child
 */

type TriggerTypes = 'manual' | 'click' | 'hover' | 'focus';

export type Alignment = 'middle' | 'left' | 'right' | 'top' | 'bottom';
export type Placement = 'top' | 'bottom' | 'left' | 'right';

export type Props = {
  alignment?: Alignment | undefined;
  placement?: Placement | undefined;
  /** Delay for showing & hiding the layer (milliseconds) */
  delay?: number | undefined;
  /** Milliseconds */
  delayHide?: number | undefined;
  /** Milliseconds */
  delayShow?: number | undefined;
  hideLayerOnScroll?: boolean | undefined;
  /** this prop works together with hideLayerOnScroll, use it
   *  to control which element you want to listen the scroll event from,
   *  if omitted we will do our best to figure it out.
   */
  scrollParent?: Node;
  /** Layer instance to display */
  layer: React.ReactElement;
  trigger?: TriggerTypes | TriggerTypes[];
  useLayerAsTrigger?: boolean;
  /** Set `width` Layer to trigger instance `width` */
  useContextWidth?: boolean | undefined;
  /** Override Layer `z-index` determination – Use with caution (mixing non-Layer overlays) */ zIndex?:
    | number
    | undefined;
  className?: string | undefined;
  activeClassName?: string | undefined;
  onHide?: Function | undefined;
  onShow?: Function | undefined;
  defaultLayerShown?: boolean | undefined;
  children: React.ReactNode | undefined;
  fixedPlacement?: boolean | undefined;
  /** For cases when you don't want `cursor: pointer` */
  noPointer?: boolean;
  /**
   * Make this modal solution recalculate the modal position when
   * its props change (i.e. the layer content changes).
   * This is what you would expect from any modal solution but unfortunately
   * this one hasn't been designed with that in mind and that is why
   * I am hiding this _normal_ modal behind a prop to make sure nothing else breaks.
   * Careful with the performance of this mode since this modal hasn't been optimized
   * for working with it.
   * */
  dynamicMode?: boolean;
  /** For cases when the trigger is inside another component that can capture an event */
  stopPropagation?: boolean;
};

type State = {
  layerTop: number;
  layerRight: number | null;
  layerLeft: number;
  layerAlignment: Alignment;
  layerPlacement: Placement;
  layerShown: boolean;
};

class LayerTrigger extends React.Component<Props, State> {
  static defaultProps: {
    alignment: Alignment;
    placement: Placement;
    trigger: TriggerTypes | TriggerTypes[];
    useContextWidth?: boolean;
    fixedPlacement: boolean;
  } = {
    alignment: 'middle',
    placement: 'right',
    trigger: ['hover', 'focus'],
    useContextWidth: false,
    fixedPlacement: false,
  };

  constructor(props: Props) {
    super(props);
    const { alignment, placement } = props;
    if (alignment !== 'middle') {
      if (includes(['top', 'bottom'], placement)) {
        if (!includes(['left', 'right'], alignment)) {
          throw new Error(
            `Invalid prop \`alignment\` of value \`${alignment}\` supplied to expected one of ["left", "right"] when \`placement\` is \`${placement}\`.`
          );
        }
      } else if (includes(['left', 'right'], placement)) {
        if (!includes(['top', 'bottom'], alignment)) {
          throw new Error(
            `Invalid prop \`alignment\` of value \`${alignment}\` supplied to expected one of ["top", "bottom"] when \`placement\` is \`${placement}\`.`
          );
        }
      }
    }
    this.state = {
      layerTop: -9999,
      layerRight: null,
      layerLeft: 0,
      layerAlignment: props.alignment!,
      layerPlacement: props.placement!,
      layerShown: props.defaultLayerShown || false,
    };
  }

  componentDidMount() {
    this._mounted = true;
    this.updateLayerPosition();
    this.node = ReactDOM.findDOMNode(this);
    setTimeout(this.updateLayerPosition, 0);
    if (this.props.hideLayerOnScroll) {
      this._scrollParent = this.props.scrollParent || findScrollParent(this.node);
      if (this._scrollParent) {
        this._scrollParent.addEventListener('scroll', this.hideLayer, true);
      }
    }
    window.addEventListener('resize', this.updateLayerPosition);
  }

  componentDidUpdate(prevProps: Props, prevState: State) {
    if (this.props.trigger === 'manual') {
      if (this._hoverDelay !== null) {
        clearTimeout(this._hoverDelay);
        this._hoverDelay = null;
      }
    }
    if (this.props.dynamicMode) {
      this.updateLayerPosition();
    }
    if (this.state.layerShown !== prevState.layerShown) {
      // this is put at the end of the event queue because
      // the component need to be rendered before we can
      // calculate it's correct size and see if it fits
      // in the view port or not.
      window.setTimeout(this.updateLayerPosition, 0);
    }

    if (prevState.layerShown && !this.state.layerShown) {
      this._unmountLayerTarget();
    }
  }

  componentWillUnmount() {
    if (this._hoverDelay) {
      clearTimeout(this._hoverDelay);
    }
    window.removeEventListener('resize', this.updateLayerPosition);
    if (this._scrollParent) {
      this._scrollParent.removeEventListener('scroll', this.hideLayer, true);
    }
    if (this.props.trigger === 'manual' && this.state.layerShown) {
      this.hideLayer();
    }
    this._unmountLayerTarget();
    this._mounted = false;
  }

  _mounted = false;
  _layerTarget: HTMLElement | null = null;
  _layerInstance: HTMLElement | null = null;
  node: null | Element | Text = null;
  _hoverDelay: NodeJS.Timer | null = null;
  _scrollParent: any = null;

  isLayerShown = () => {
    return this.state.layerShown;
  };

  showLayer = () => {
    if (!this._mounted) {
      console.warn('showLayer() while unmounted');
      return;
    }
    this._mountLayerTarget();
    this.setState({ layerShown: true });
    if (this.props.onShow) {
      this.props.onShow();
    }
  };

  hideLayer = () => {
    // @todo: we need to keep this timeout id in a prop and clear the timeout on destroy. Otherwise this will cause errors on tests.
    setTimeout(() => {
      if (!this._mounted) {
        console.warn('hideLayer() while unmounted');
        return;
      }
      if (this.state.layerShown) {
        this.setState({ layerShown: false });
        if (this.props.onHide) {
          this.props.onHide();
        }
      }
    }, 100);
  };

  toggleLayer = () => {
    if (this.state.layerShown) {
      this.hideLayer();
    } else {
      this.showLayer();
    }
  };

  _mountLayerTarget = () => {
    if (!this._layerTarget) {
      const container = this.getContainerDOMNode();
      if (!container) {
        throw new Error('LayerTrigger container missing in _mountLayerTarget');
      }
      this._layerTarget = window.document.createElement('div');
      container.appendChild(this._layerTarget);
    }
  };

  _unmountLayerTarget = () => {
    if (this._layerTarget) {
      if (this._layerTarget.parentNode) {
        this._layerTarget.parentNode.removeChild(this._layerTarget);
      }
      this._layerTarget = null;
    }
  };

  getLayerDOMNode = (): HTMLElement | null => {
    if (!this._mounted) {
      throw new Error('getLayerDOMNode(): A component must be mounted to have a DOM node.');
    }
    if (this._layerInstance) {
      const foundNode = ReactDOM.findDOMNode(this._layerInstance);
      if (!(foundNode instanceof HTMLElement)) {
        throw new Error(
          `LayerTrigger getLayerDOMNode found ${typeof foundNode}, expected HTMLElement`
        );
      }
      return foundNode;
    }
    return null;
  };

  getContainerDOMNode = (): HTMLElement | null => {
    const element = this.node;
    return ((element && element.ownerDocument) || window.document).body;
  };

  getLayerRect = () => {
    const layerNode = this.getLayerDOMNode();
    if (!layerNode) {
      throw new Error('LayerTrigger layer missing in getLayerRect');
    }
    return layerNode.getBoundingClientRect();
  };

  getPosition = () => {
    const { node: thisNode } = this;
    const container = this.getContainerDOMNode();
    if (!container) {
      throw new Error('LayerTrigger container missing in getPosition');
    }
    if (!(thisNode instanceof HTMLElement)) {
      throw new Error(`LayerTrigger node not an HTMLElement, found: ${typeof thisNode}`);
    }

    const offset =
      container.tagName === 'BODY'
        ? DOM.getOffset(thisNode as HTMLElement | Text | null)
        : DOM.getPosition(thisNode as HTMLElement, container);
    if (isFixed(thisNode)) {
      const scrollPosition = DOM.getScrollPosition();
      offset.left -= scrollPosition.left;
      offset.top -= scrollPosition.top;
    }

    return Object.assign({}, offset, {
      height: thisNode.offsetHeight,
      width: thisNode.offsetWidth,
    });
  };

  calcLayerPosition = (
    placement: Placement | string,
    alignment: Alignment
  ): { top: number; left: number; right: number } => {
    const childOffset = this.getPosition();
    const layer = this.getLayerDOMNode();
    if (!layer) {
      throw new Error('LayerTrigger layer missing in calcLayerPosition');
    }
    const position: any = {};
    switch (placement) {
      case 'right':
        position.left = childOffset.left + childOffset.width;
        break;
      case 'left':
        position.left = childOffset.left - layer.offsetWidth;
        break;
      case 'top':
        position.top = childOffset.top - layer.offsetHeight;
        break;
      case 'bottom':
        position.top = childOffset.top + childOffset.height;
        break;
      default:
        break;
    }
    if (this.props.useContextWidth && (placement === 'top' || placement === 'bottom')) {
      const width = window.document.documentElement.clientWidth;
      position.left = childOffset.left;
      position.right = width - (childOffset.left + childOffset.width);
    } else {
      switch (alignment) {
        case 'right':
          position.left = childOffset.left + childOffset.width - layer.offsetWidth;
          break;
        case 'left':
          position.left = childOffset.left;
          break;
        case 'top':
          position.top = childOffset.top;
          break;
        case 'bottom':
          position.top = childOffset.top + childOffset.height - layer.offsetHeight;
          break;
        default:
        case 'middle':
          if (placement === 'left' || placement === 'right') {
            position.top = childOffset.top + childOffset.height / 2 - layer.offsetHeight / 2;
          } else {
            position.left = childOffset.left + childOffset.width / 2 - layer.offsetWidth / 2;
          }
          break;
      }
    }
    return position;
  };

  fitsWithinViewport = (left: number, top: number, right: number, bottom: number) => {
    const node = this.node;
    const bounds = isFixed(node)
      ? DOM.getOffset(document.documentElement)
      : {
          top: window.document.body.scrollTop,
          left: window.document.body.scrollLeft,
        };
    const width = window.document.documentElement.clientWidth;
    const height = window.document.documentElement.clientHeight;
    return (
      top >= bounds.top &&
      bottom <= bounds.top + height &&
      left >= bounds.left &&
      right <= bounds.left + width
    );
  };

  handleClick = (event: any) => {
    event.preventDefault();
    if (this.props.stopPropagation) {
      event.stopPropagation();
    }
    this.toggleLayer();
  };

  handleDelayedHide = () => {
    if (this._hoverDelay !== null) {
      clearTimeout(this._hoverDelay);
      this._hoverDelay = null;
      return;
    }
    let delay = this.props.delayHide || this.props.delay;
    // when layer boundaries are considered, there is always, at least, a small delay, so that the mouse can skip a small gap between the trigger and the layer
    if (this.props.useLayerAsTrigger && !delay) {
      delay = 300;
    }
    if (!delay) {
      this.hideLayer();
    } else {
      this._hoverDelay = setTimeout(() => {
        this._hoverDelay = null;
        this.hideLayer();
      }, delay);
    }
  };

  handleDelayedShow = () => {
    if (this._hoverDelay !== null) {
      clearTimeout(this._hoverDelay);
      this._hoverDelay = null;
      return;
    }
    const delay = this.props.delayShow || this.props.delay;
    if (!delay) {
      this.showLayer();
      return;
    }
    this._hoverDelay = setTimeout(() => {
      this._hoverDelay = null;
      this.showLayer();
    }, delay);
  };

  handleFocus = () => {
    if (!KeyState.isKeyDown()) {
      return;
    }
    this.handleDelayedShow();
  };

  setLayerPosition(
    layerPosition: { left: number; top: number; right: number },
    placement: Placement,
    alignment: Alignment
  ) {
    const { left, right, top } = layerPosition;
    const comp = this;
    this.setState(
      {
        layerLeft: left,
        layerRight: right,
        layerTop: top,
        layerPlacement: placement,
        layerAlignment: alignment,
      },
      function () {
        // NOTE: Hacky but because we don't automatically re-render after the
        // pending state transaction is completed, and LayerWithArrow depends
        // on LayerTrigger to position its arrow, the arrow will never be
        // drawn properly when a Hovercard is first displayed. By rerendering
        // here, we ensure that the child LayerWithArrow updates its arrow
        // position correctly and redraws itself.
        // TODO: Fix this arrow display problem in a less hacky way.
        comp.forceUpdate();
      }
    );
  }

  updateLayerPosition = throttle(() => {
    if (!this._mounted || !this.state.layerShown) {
      return;
    }
    const attempts: { [key in Alignment]?: any } = {};
    const layer = this.getLayerDOMNode();
    if (!layer) {
      throw new Error('LayerTrigger layer missing in updateLayerPosition');
    }
    const width = layer.offsetWidth;
    const height = layer.offsetHeight;
    const { placement, alignment, fixedPlacement } = this.props;
    const preferredLayerPosition = this.calcLayerPosition(placement!, alignment!);
    if (fixedPlacement) {
      this.setLayerPosition(preferredLayerPosition, placement!, alignment!);
    } else {
      const found = [placement!, OPPOSITES[placement!]].concat(PLACEMENTS).some((pt: Alignment) => {
        if (pt in attempts) {
          return false;
        }
        attempts[pt] = {};
        return [alignment!]
          .concat(pt === 'left' || pt === 'right' ? VERTICAL_ALIGNMENTS : HORIZONTAL_ALIGNMENTS)
          .some((at) => {
            if (at in attempts[pt]) {
              return false;
            }
            attempts[pt][at] = true;
            const layerPosition = this.calcLayerPosition(pt, at);
            const { left, top } = layerPosition;
            if (this.fitsWithinViewport(left, top, left + width, top + height)) {
              // @ts-expect-error ts-migrate(2345) FIXME: Argument of type 'Alignment' is not assignable to ... Remove this comment to see the full error message
              this.setLayerPosition(layerPosition, pt, at);
              return true;
            }
            return false;
          });
      });
      if (!found) {
        this.setLayerPosition(preferredLayerPosition, placement!, alignment!);
      }
    }
  }, 100);

  renderLayer = () => {
    const { node } = this;
    const { layer } = this.props;

    const extraProps: {
      onMouseEnter: null | (() => void);
      onMouseLeave: null | (() => void);
      onClick: null | ((event: any) => void);
    } = {
      onMouseEnter: null,
      onMouseLeave: null,
      onClick: null,
    };
    if (isOneOf('hover', this.props.trigger!) && this.props.useLayerAsTrigger) {
      extraProps.onMouseEnter = this.handleDelayedShow;
      extraProps.onMouseLeave = this.handleDelayedHide;
    }

    const clonedLayer = React.cloneElement(layer, {
      onRequestHide: createChainedFunction(
        this.hideLayer,
        idx(layer, (_) => _.props.onRequestHide)
      ),
      placement: this.state.layerPlacement,
      alignment: this.state.layerAlignment,
      position: {
        left: this.state.layerLeft,
        right: this.state.layerRight,
        top: this.state.layerTop,
      },
      context: node,
      // we add 10 to make sure the z-index of a layer that appears
      // on top of other layers is higher than the z-index of the
      // underlying layer
      zIndex: this.props.zIndex || findZIndex(node) + 10,
      fixed: isFixed(node),
      ref: (portal: any) => (this._layerInstance = portal),
      ...(this.props.useLayerAsTrigger ? { ...extraProps } : undefined),
    });

    if (!this._layerTarget) {
      throw new Error('LayerTrigger.renderLayer missing this._layerTarget');
    }
    return ReactDOM.createPortal(clonedLayer, this._layerTarget);
  };

  render() {
    const { activeClassName, children, className, trigger, noPointer } = this.props;
    const props: any = {};
    const child: any = React.Children.only(children);
    if (isOneOf('click', trigger!)) {
      props.onClick = createChainedFunction(this.handleClick, child.props.onClick);
    }
    if (isOneOf('hover', trigger!)) {
      props.onMouseEnter = createChainedFunction(this.handleDelayedShow, child.props.onMouseEnter);
      props.onMouseLeave = createChainedFunction(this.handleDelayedHide, child.props.onMouseLeave);
    }
    if (isOneOf('focus', trigger!)) {
      props.onFocus = createChainedFunction(this.handleFocus, child.props.onFocus);
      props.onBlur = createChainedFunction(this.handleDelayedHide, child.props.onBlur);
    }
    return [
      React.cloneElement(
        child,
        Object.assign({}, props, {
          className: cx(
            !noPointer && 'LayerTrigger',
            child.props.className,
            className,
            this.state.layerShown && activeClassName
          ),
          key: 'trigger',
        })
      ),
      this.state.layerShown && this.renderLayer(),
    ];
  }
}

export default LayerTrigger;
